import logger from '../utils/logger';
import { supabase } from '../utils/supabaseClient';

class AuthService {
  constructor() {
    this.session = null;
    this.user = null;
    this.role = null; // 'admin' | 'user' | null
    this.termsVersion = null;
    this.termsAcceptedAt = null;

    // Keep in sync with Supabase session changes (login, logout, token refresh).
    supabase.auth.onAuthStateChange((_event, session) => {
      this.session = session;
      this.user = session?.user ?? null;
      if (session?.user) {
        this._fetchProfileState(session.user.id);
      } else {
        this.role = null;
        this.termsVersion = null;
        this.termsAcceptedAt = null;
      }
    });

    // Hydrate from any existing session on page load.
    supabase.auth.getSession().then(({ data: { session } }) => {
      this.session = session;
      this.user = session?.user ?? null;
      if (session?.user) {
        this._fetchProfileState(session.user.id);
      }
    });
  }

  /**
   * Fetch the user's profile state from the profiles table and cache it.
   * @param {string} userId
   */
  async _fetchProfileState(userId) {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('role, terms_version, terms_accepted_at')
        .eq('id', userId)
        .single();

      if (error) {
        logger.error('Error fetching user profile state:', error);
        this.role = 'user'; // default to least-privileged
        this.termsVersion = null;
        this.termsAcceptedAt = null;
        return {
          role: this.role,
          termsVersion: this.termsVersion,
          termsAcceptedAt: this.termsAcceptedAt,
        };
      }
      this.role = data?.role ?? 'user';
      this.termsVersion = data?.terms_version ?? null;
      this.termsAcceptedAt = data?.terms_accepted_at ?? null;
      return {
        role: this.role,
        termsVersion: this.termsVersion,
        termsAcceptedAt: this.termsAcceptedAt,
      };
    } catch (err) {
      logger.error('Unexpected error fetching profile state:', err);
      this.role = 'user';
      this.termsVersion = null;
      this.termsAcceptedAt = null;
      return {
        role: this.role,
        termsVersion: this.termsVersion,
        termsAcceptedAt: this.termsAcceptedAt,
      };
    }
  }

  async register(email, password) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) {
      logger.error('Registration error:', error);
      throw error;
    }
    return data;
  }

  async login(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      logger.error('Login error:', error);
      throw error;
    }
    // Await the role fetch so isAdmin() is accurate the moment login() resolves.
    if (data?.user) {
      await this._fetchProfileState(data.user.id);
    }
    return data;
  }

  /**
   * Sends a password-recovery email that links back to /reset-password.
   * The redirect origin must be allowlisted in Supabase Auth → URL
   * Configuration, or Supabase falls back to the bare Site URL.
   */
  async requestPasswordReset(email) {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) {
      logger.error('Password reset request error:', error);
      throw error;
    }
  }

  /**
   * Sets a new password for the current session (a recovery-link session or
   * a normal logged-in session).
   */
  async updatePassword(newPassword) {
    const { data, error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) {
      logger.error('Password update error:', error);
      throw error;
    }
    return data;
  }

  async signInWithGoogle() {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/auth/callback` },
    });
    if (error) {
      logger.error('Google sign-in error:', error);
      throw error;
    }
    return data;
  }

  async handleAuthCallback() {
    const { data, error } = await supabase.auth.getSession();
    if (error) {
      logger.error('Auth callback error:', error);
      throw error;
    }

    if (data?.session?.user) {
      this.session = data.session;
      this.user = data.session.user;
      await this._fetchProfileState(data.session.user.id);
      return data;
    }

    // Session not yet available — the PKCE code exchange may still be in flight.
    // Wait for the SIGNED_IN event rather than returning null and falling back to /login.
    return new Promise((resolve, reject) => {
      let timerId;

      const { data: listenerData } = supabase.auth.onAuthStateChange(async (event, session) => {
        if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && session) {
          clearTimeout(timerId);
          listenerData.subscription.unsubscribe();
          this.session = session;
          this.user = session.user;
          if (session.user) {
            await this._fetchProfileState(session.user.id);
          }
          resolve({ session });
        }
      });

      timerId = setTimeout(() => {
        listenerData.subscription.unsubscribe();
        reject(new Error('Auth callback timed out'));
      }, 10000);
    });
  }

  async getDashboardRoute() {
    let session = this.session;

    if (!session) {
      const { data, error } = await supabase.auth.getSession();
      if (error) {
        logger.error('Error resolving dashboard route:', error);
        throw error;
      }

      session = data?.session ?? null;
      this.session = session;
    }

    const user = session?.user ?? this.user;
    this.user = user ?? null;

    if (!user) {
      return '/login';
    }

    const { role } = await this._fetchProfileState(user.id);
    return role === 'admin' ? '/admin' : '/dashboard';
  }

  async refreshProfileState() {
    const user = this.user;

    if (!user) {
      this.role = null;
      this.termsVersion = null;
      this.termsAcceptedAt = null;
      return {
        role: null,
        termsVersion: null,
        termsAcceptedAt: null,
      };
    }

    return this._fetchProfileState(user.id);
  }

  async acceptCurrentTerms(version = 'v1') {
    const { data, error } = await supabase.rpc('accept_current_terms', {
      accepted_version: version,
    });

    if (error) {
      logger.error('Error accepting current terms:', error);
      throw error;
    }

    this.termsVersion = data?.terms_version ?? version;
    this.termsAcceptedAt = data?.terms_accepted_at ?? new Date().toISOString();

    return {
      termsVersion: this.termsVersion,
      termsAcceptedAt: this.termsAcceptedAt,
    };
  }

  async logout() {
    const { error } = await supabase.auth.signOut();
    if (error) {
      logger.error('Logout error:', error);
      throw error;
    }
  }

  getUser() {
    return this.user;
  }

  isLoggedIn() {
    return this.session !== null;
  }

  /**
   * Returns true only if the authenticated user has the 'admin' role
   * in the profiles table. Regular (user-level) accounts return false.
   */
  isAdmin() {
    return this.isLoggedIn() && this.role === 'admin';
  }

  /**
   * The current session's access token, or null when logged out. Used by
   * languageModelService to present verifiable identity to the chat proxy
   * (verified admins are metered against a per-user rate pool).
   */
  getAccessToken() {
    return this.session?.access_token ?? null;
  }

  /**
   * Returns the cached role string ('admin' | 'user' | null).
   */
  getRole() {
    return this.role;
  }

  hasAcceptedTerms(version = 'v1') {
    return Boolean(this.termsAcceptedAt) && this.termsVersion === version;
  }

  getTermsAcceptance() {
    return {
      termsVersion: this.termsVersion,
      termsAcceptedAt: this.termsAcceptedAt,
      hasAcceptedCurrentTerms: this.hasAcceptedTerms(),
    };
  }
}

export const authService = new AuthService();
